// Sync modal — reconciles the manpower schedule with the Projects tab: lists
// every project that has schedule entries alongside its Projects-tab status,
// flags jobs that are finished there (Complete / Canceled / archived) but still
// have crew booked from today forward, and clears that future crew
// project-by-project. Past weeks always keep their history. Moved verbatim
// from ManpowerPage.tsx.

import { useState, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { manpowerApi, type MpSyncRow } from '@/lib/apiClient';
import { invalidateReadinessQueries, invalidateStartAlignmentQueries } from '@/lib/queryClient';
import { useFocusTrap } from '@/lib/hooks/useFocusTrap';
import { easternTodayYmd } from './lib/gridDates';
import { TH } from './lib/crewStyles';

export default function SyncModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const modalRef = useRef<HTMLDivElement>(null);
  useFocusTrap(modalRef, { onEscape: onClose });

  // Eastern, matching the schedule the sync is being checked against (#477).
  const todayYmd = easternTodayYmd();
  const { data: rows = [], isLoading, isError, refetch } = useQuery<MpSyncRow[]>({
    queryKey: ['manpower-sync-status'],
    queryFn: () => manpowerApi.syncStatus(todayYmd),
    staleTime: 0,
  });

  const [confirmId, setConfirmId] = useState<number | null>(null);
  const [busyId, setBusyId]       = useState<number | null>(null);
  const [notice, setNotice]       = useState('');

  const isFinished = (r: MpSyncRow) =>
    r.isArchived || r.statusLabel === 'Complete' || r.statusLabel === 'Canceled';
  const isStale = (r: MpSyncRow) => isFinished(r) && r.futureEntries > 0;

  // Stale rows first (they're what this tool exists for), then job# desc.
  const sorted = [...rows].sort((a, b) => {
    if (isStale(a) !== isStale(b)) return isStale(a) ? -1 : 1;
    return b.jobNumber.localeCompare(a.jobNumber);
  });
  const staleCount = rows.filter(isStale).length;

  async function clearFuture(r: MpSyncRow) {
    setBusyId(r.projectId);
    try {
      const res = await manpowerApi.clearFutureEntries(r.projectId, todayYmd);
      setNotice(
        `Cleared ${res.deleted} day-entr${res.deleted === 1 ? 'y' : 'ies'} for ${r.jobNumber} · ${r.title}.`
        + (!isFinished(r) ? ' Still active on the Projects tab, so it keeps an empty row until it’s completed or archived there.' : ''),
      );
      setConfirmId(null);
      await refetch();
      queryClient.invalidateQueries({ queryKey: ['manpower-schedule'] });
      // ⚠️ THE SECOND clearFutureEntries CALLER. ManpowerPage has the other one,
      // and wiring only that one is how this helper acquires the same hole
      // invalidateAssignmentQueries already has a note about: N writers, and the
      // one nobody remembers is the one in a modal. Clearing future crew can
      // qualify a job for "No crew booked" on both Today pages.
      void invalidateReadinessQueries(queryClient, r.projectId);
      // …and the start-mismatch flag, whose first-crew-day input this write can
      // remove outright (#385). Same reason as the note above: N writers, and
      // the one in a modal is the one that gets forgotten.
      void invalidateStartAlignmentQueries(queryClient, r.projectId);
    } catch {
      setNotice(`Failed to clear ${r.jobNumber} — try again.`);
    } finally {
      setBusyId(null);
    }
  }

  const statusChip = (r: MpSyncRow) => {
    const finished = isFinished(r);
    const label = r.isArchived ? 'Archived' : (r.statusLabel ?? 'No status');
    return (
      <span style={{
        display: 'inline-block', padding: '2px 8px', fontSize: 10, fontWeight: 700,
        borderRadius: 'var(--ew-r-pill)', whiteSpace: 'nowrap',
        background: finished ? '#fef2f2' : 'var(--ew-green-50)',
        color:      finished ? 'var(--ew-danger)' : 'var(--ew-green-700)',
        border:     `1px solid ${finished ? '#fecaca' : 'var(--ew-green-100)'}`,
      }}>
        {label}
      </span>
    );
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div ref={modalRef} style={{ background: 'var(--ew-white)', borderRadius: 'var(--ew-r-lg)', boxShadow: 'var(--ew-shadow-3)', width: 860, maxWidth: '100%', maxHeight: '88vh', display: 'flex', flexDirection: 'column', fontFamily: 'var(--ew-font)' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid var(--ew-n-200)' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Sync with Projects Tab</h3>
          <button onClick={onClose} aria-label="Close" style={{ border: 'none', background: 'transparent', fontSize: 18, cursor: 'pointer', color: 'var(--ew-n-500)' }}>×</button>
        </div>

        <div style={{ padding: '12px 18px', overflowY: 'auto' }}>
          <p style={{ margin: '0 0 10px', fontSize: 12, color: 'var(--ew-n-500)' }}>
            Every project with manpower entries, against its Projects-tab status.
            <strong> Clear future crew</strong> removes a project from the schedule from today forward — past weeks keep their history.
            Jobs still active on the Projects tab keep an empty row until they're marked Complete/Canceled or archived there.
          </p>

          {notice && (
            <div style={{ marginBottom: 10, background: '#f0fdf4', border: '1px solid var(--ew-green-100)', borderRadius: 'var(--ew-r-md)', padding: '7px 10px', fontSize: 12, color: 'var(--ew-green-700)' }}>
              {notice}
            </div>
          )}

          {isLoading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--ew-n-400)', fontSize: 13 }}>Loading…</div>
          ) : isError ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--ew-danger)', fontSize: 13 }}>Failed to load sync status.</div>
          ) : (
            <>
              <div style={{ marginBottom: 8, fontSize: 12, fontWeight: 600, color: staleCount > 0 ? 'var(--ew-danger)' : 'var(--ew-green-700)' }}>
                {staleCount > 0
                  ? `⚠ ${staleCount} job${staleCount === 1 ? ' is' : 's are'} finished on the Projects tab but still ${staleCount === 1 ? 'has' : 'have'} crew scheduled ahead.`
                  : '✓ No finished jobs have crew scheduled ahead.'}
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={{ ...TH, textAlign: 'left', padding: '6px 10px', position: 'sticky', top: 0 }}>Job #</th>
                    <th style={{ ...TH, textAlign: 'left', padding: '6px 10px', position: 'sticky', top: 0 }}>Project</th>
                    <th style={{ ...TH, textAlign: 'left', padding: '6px 10px', position: 'sticky', top: 0 }}>Division</th>
                    <th style={{ ...TH, textAlign: 'left', padding: '6px 10px', position: 'sticky', top: 0 }}>Projects-tab status</th>
                    <th style={{ ...TH, textAlign: 'left', padding: '6px 10px', position: 'sticky', top: 0 }}>Scheduled ahead</th>
                    <th style={{ ...TH, textAlign: 'right', padding: '6px 10px', position: 'sticky', top: 0 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((r) => {
                    const stale = isStale(r);
                    return (
                      <tr key={r.projectId} style={{ background: stale ? '#fef2f2' : 'var(--ew-white)', borderBottom: '1px solid var(--ew-n-100)' }}>
                        <td style={{ padding: '6px 10px', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', color: 'var(--ew-n-500)' }}>{r.jobNumber}</td>
                        <td style={{ padding: '6px 10px', fontWeight: 500 }}>{r.title}</td>
                        <td style={{ padding: '6px 10px', whiteSpace: 'nowrap', color: 'var(--ew-n-500)' }}>{r.divisionLabel ?? '—'}</td>
                        <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>{statusChip(r)}</td>
                        <td style={{ padding: '6px 10px', whiteSpace: 'nowrap', color: r.futureEntries > 0 ? 'var(--ew-dark)' : 'var(--ew-n-400)' }}>
                          {r.futureEntries > 0
                            ? `${r.futureCrew} crew over ${r.futureEntries} day${r.futureEntries === 1 ? '' : 's'}${r.lastCrewDate ? ` (thru ${String(r.lastCrewDate).slice(5, 10).replace('-', '/')})` : ''}`
                            : 'Nothing ahead'}
                        </td>
                        <td style={{ padding: '6px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {r.futureEntries > 0 && (
                            confirmId === r.projectId ? (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--ew-danger)' }}>Sure?</span>
                                <button className="btn btn-danger" style={{ fontSize: 10, padding: '2px 8px' }} disabled={busyId === r.projectId} onClick={() => void clearFuture(r)}>
                                  {busyId === r.projectId ? 'Clearing…' : 'Yes, clear'}
                                </button>
                                <button className="btn btn-secondary" style={{ fontSize: 10, padding: '2px 8px' }} onClick={() => setConfirmId(null)}>No</button>
                              </span>
                            ) : (
                              <button className="btn btn-secondary" style={{ fontSize: 10, padding: '2px 8px', color: 'var(--ew-danger)' }} onClick={() => setConfirmId(r.projectId)}>
                                Clear future crew
                              </button>
                            )
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {sorted.length === 0 && (
                    <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: 'var(--ew-n-400)' }}>No projects have schedule entries.</td></tr>
                  )}
                </tbody>
              </table>
            </>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 18px', borderTop: '1px solid var(--ew-n-200)' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
